// Huésped titular = cliente de financiero.partner (script 926). Buscador de
// clientes existentes (nombre o identificación) para no volver a digitar al
// huésped que regresa, y la lista de clientes que comparten las pantallas.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UserCheck, X } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { fieldCls } from './shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
export interface ClienteOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[]; email?: string | null; celular?: string | null }

export const TIPOS_IDENT = [['CEDULA', 'Cédula'], ['RUC', 'RUC'], ['PASAPORTE', 'Pasaporte']] as const;

/** Clientes de la empresa (sin el consumidor final, que no sirve para un huésped). */
export function useClientes() {
  const { data = [] } = useQuery<ClienteOpt[]>({
    queryKey: ['fin-partners'],
    queryFn: async () => (await apiClient.post<ClienteOpt[]>('/rpc/sri_partners', {}, FIN)).data || [],
  });
  return useMemo(() => data.filter((p) => (p.tipo_partner || []).includes('cliente') && p.identificacion !== '9999999999999'), [data]);
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function BuscadorCliente({ clientes, seleccionado, onSelect }: {
  clientes: ClienteOpt[]; seleccionado: ClienteOpt | null; onSelect: (c: ClienteOpt | null) => void;
}) {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  const resultados = useMemo(() => {
    const t = norm(q.trim());
    if (t.length < 2) return [];
    return clientes.filter((c) => norm(c.razon_social).includes(t) || (c.identificacion ?? '').toLowerCase().includes(t)).slice(0, 8);
  }, [q, clientes]);

  if (seleccionado) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-800 dark:bg-emerald-900/20">
        <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-emerald-600" />
          <b>{seleccionado.razon_social}</b><span className="text-muted-foreground">{seleccionado.identificacion}</span>
          <span className="text-xs text-emerald-700 dark:text-emerald-400">cliente existente</span></span>
        <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => { onSelect(null); setQ(''); }}>
          <X className="h-3.5 w-3.5" /> Cambiar
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <input className={`${fieldCls} pl-8`} value={q} placeholder="Buscar cliente existente por nombre, cédula, RUC o pasaporte…"
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }} onFocus={() => setAbierto(true)} onBlur={() => setTimeout(() => setAbierto(false), 150)} />
      {abierto && q.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
          {resultados.map((c) => (
            <button key={c.id} type="button" className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()} onClick={() => { onSelect(c); setAbierto(false); }}>
              <span>{c.razon_social}</span><span className="font-mono text-xs text-muted-foreground">{c.identificacion}</span>
            </button>
          ))}
          {resultados.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Sin coincidencias: registre los datos abajo y se creará como cliente nuevo.</div>}
        </div>
      )}
    </div>
  );
}
